import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { CheckCircle, Copy } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../common/components/Button";
import { toast } from "../../common/components/Toast";
import { SPACING } from "../../common/constants/spacing";
import { createInvite } from "../api/invites";
import AssetSelector from "./AssetSelector";

function InviteUserModal({
  showInviteModal,
  setShowInviteModal,
  currentUserRole,
}) {
  const { t } = useTranslation("members");
  const defaultRole = currentUserRole === "owner" ? "producer" : "viewer";
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(defaultRole);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [revenueShare, setRevenueShare] = useState("");
  const [loading, setLoading] = useState(false);
  const [inviteCreated, setInviteCreated] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  const getRoleOptions = () => {
    if (currentUserRole === "owner") {
      return [
        { value: "producer", label: t("roles.producer") },
        { value: "owner", label: t("roles.owner") },
        { value: "viewer", label: t("roles.viewer") },
        { value: "co_producer", label: t("roles.co_producer") },
      ];
    }
    return [
      { value: "viewer", label: t("roles.viewer") },
      { value: "co_producer", label: t("roles.co_producer") },
    ];
  };

  const needsAssets = ["producer", "viewer", "co_producer"].includes(role);

  const handleCloseModal = () => {
    setShowInviteModal(false);
    setEmail("");
    setRole(defaultRole);
    setSelectedAssets([]);
    setRevenueShare("");
    setInviteCreated(false);
    setInviteData(null);
    setCopySuccess(false);
  };

  const handleRoleChange = (newRole) => {
    setRole(newRole);
    if (newRole === "owner") {
      setSelectedAssets([]);
    }
    if (newRole !== "co_producer") {
      setRevenueShare("");
    }
  };

  const handleSendInvite = async () => {
    if (needsAssets && selectedAssets.length === 0) {
      toast.error(t("inviteModal.assetsRequired"));
      return;
    }

    if (
      role === "co_producer" &&
      (!revenueShare || parseFloat(revenueShare) <= 0)
    ) {
      toast.error(t("inviteModal.revenueShareRequired"));
      return;
    }

    const invitePayload = {
      email,
      role,
      asset_ids: needsAssets ? selectedAssets : [],
      ...(role === "co_producer" && {
        revenue_share_percentage: parseFloat(revenueShare),
      }),
    };

    setLoading(true);
    try {
      const createdInvite = await createInvite(invitePayload);
      setInviteData(createdInvite);
      setInviteCreated(true);
      toast.success(t("inviteModal.inviteSuccess"));
    } catch (error) {
      console.error("Error creating invite:", error);

      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error(t("inviteModal.inviteError"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (inviteData?.invite_link) {
      try {
        await navigator.clipboard.writeText(inviteData.invite_link);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = inviteData.invite_link;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand("copy");
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
        } catch (fallbackErr) {
          toast.error(t("inviteModal.copyFailed"));
        }
        document.body.removeChild(textArea);
      }
    }
  };

  const handleSendAnother = () => {
    setEmail("");
    setRole(defaultRole);
    setSelectedAssets([]);
    setRevenueShare("");
    setInviteCreated(false);
    setInviteData(null);
    setCopySuccess(false);
  };

  return (
    <Dialog
      open={showInviteModal}
      onClose={handleCloseModal}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {inviteCreated ? t("inviteModal.titleSuccess") : t("inviteModal.title")}
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 3 }}>
        {!inviteCreated ? (
          // Invite Form
          <Box sx={{ mt: SPACING.sm }}>
            <TextField
              fullWidth
              label={t("inviteModal.emailLabel")}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("inviteModal.emailPlaceholder")}
              sx={{ mb: 3 }}
            />

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>{t("inviteModal.roleLabel")}</InputLabel>
              <Select
                value={role}
                label={t("inviteModal.roleLabel")}
                onChange={(e) => handleRoleChange(e.target.value)}
              >
                {getRoleOptions().map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {role === "co_producer" && (
              <TextField
                fullWidth
                label={t("inviteModal.revenueShareLabel")}
                type="number"
                value={revenueShare}
                onChange={(e) => setRevenueShare(e.target.value)}
                placeholder={t("inviteModal.revenueSharePlaceholder")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
                inputProps={{ min: 0.01, max: 100, step: "0.01" }}
                helperText={t("inviteModal.revenueShareInfo")}
                sx={{ mb: 3 }}
              />
            )}

            {needsAssets && (
              <AssetSelector
                selectedAssets={selectedAssets}
                onChange={setSelectedAssets}
              />
            )}

            <Alert severity="info" sx={{ mb: 3 }}>
              {email
                ? t("inviteModal.infoMessage")
                : t("inviteModal.noEmailInfo")}
            </Alert>

            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button
                variant="secondary"
                onClick={handleCloseModal}
                disabled={loading}
              >
                {t("inviteModal.cancel")}
              </Button>
              <Button
                variant="contained"
                onClick={handleSendInvite}
                disabled={loading}
              >
                {loading
                  ? t("inviteModal.sending")
                  : email
                    ? t("inviteModal.sendInvite")
                    : t("inviteModal.createLink")}
              </Button>
            </Box>
          </Box>
        ) : (
          // Success State
          <Box>
            <Alert severity="success" sx={{ my: SPACING.lg }}>
              <Typography
                variant="bodyMd"
                component="p"
                sx={{ fontWeight: 500 }}
              >
                {inviteData?.email
                  ? t("inviteModal.successMessage")
                  : t("inviteModal.linkCreated")}
              </Typography>
              {inviteData?.email && (
                <Typography variant="bodySm" sx={{ mt: 1 }}>
                  {t("inviteModal.emailSentTo")}{" "}
                  <strong>{inviteData.email}</strong>{" "}
                  {t("inviteModal.withLink")}
                </Typography>
              )}
            </Alert>

            <Typography variant="h3" sx={{ mb: SPACING.sm }}>
              {t("inviteModal.inviteDetails")}
            </Typography>

            <Box sx={{ mb: 0.5, display: "flex", gap: 1 }}>
              <Typography color="text.secondary">
                {t("inviteModal.email")}{" "}
              </Typography>
              <Typography sx={{ fontWeight: 500 }}>
                {inviteData?.email}
              </Typography>
            </Box>

            <Box sx={{ mb: 0.5, display: "flex", gap: 1 }}>
              <Typography color="text.secondary">
                {t("inviteModal.role")}{" "}
              </Typography>
              <Typography sx={{ fontWeight: 500, textTransform: "capitalize" }}>
                {inviteData?.role}
              </Typography>
            </Box>

            {inviteData?.asset_access && inviteData.asset_access.length > 0 && (
              <Box sx={{ mb: SPACING.xs }}>
                <Typography color="text.secondary" component="p">
                  {t("inviteModal.assetAccess")}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {inviteData.asset_access.map((assetAccess) => (
                    <Chip
                      key={assetAccess.asset_id}
                      label={assetAccess.asset_title}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Typography variant="h3" sx={{ mt: SPACING.lg }}>
              {t("inviteModal.inviteLink")}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1 }}>
              <TextField
                fullWidth
                value={inviteData?.invite_link || ""}
                InputProps={{
                  readOnly: true,
                  sx: { fontSize: "0.875rem", py: 0.6 },
                }}
                size="small"
              />
              <Button
                variant="outlined"
                onClick={handleCopyLink}
                startIcon={
                  copySuccess ? <CheckCircle size={18} /> : <Copy size={18} />
                }
                color={copySuccess ? "success" : "primary"}
                sx={{ minWidth: "120px" }}
              >
                {copySuccess
                  ? t("inviteModal.copied")
                  : t("inviteModal.copyLink")}
              </Button>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
                mt: SPACING.lg,
              }}
            >
              <Button variant="secondary" onClick={handleSendAnother}>
                {t("inviteModal.sendAnother")}
              </Button>
              <Button variant="contained" onClick={handleCloseModal}>
                {t("inviteModal.done")}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default InviteUserModal;
